import { t } from '@/i18n';

/**
 * The keys of the Mac's quick-send shortcut. The shell registers it with the system (Carbon hot keys), which names
 * a key by its virtual key code; the page records it from `KeyboardEvent.code`, which names the key by its place on
 * the keyboard, whatever the layout. These are the codes of the ANSI/ISO layout (HIToolbox `kVK_…`).
 */

/** Modifier bits shared with the shell (apps/apple/Bridge/QuickSendRules.swift). */
const MOD_COMMAND = 1;
const MOD_OPTION = 2;
const MOD_CONTROL = 4;
const MOD_SHIFT = 8;

export interface Shortcut {
  readonly keyCode: number;
  readonly modifiers: number;
  /** What the person reads: `⌃⌥B`. */
  readonly label: string;
}

const LETTERS: Readonly<Record<string, number>> = {
  A: 0, S: 1, D: 2, F: 3, H: 4, G: 5, Z: 6, X: 7, C: 8, V: 9, B: 11, Q: 12, W: 13, E: 14, R: 15, Y: 16, T: 17,
  O: 31, U: 32, I: 34, P: 35, L: 37, J: 38, K: 40, N: 45, M: 46,
};
const DIGITS: Readonly<Record<string, number>> = { '1': 18, '2': 19, '3': 20, '4': 21, '6': 22, '5': 23, '9': 25, '7': 26, '8': 28, '0': 29 };
const FUNCTION_KEYS: Readonly<Record<string, number>> = {
  F1: 122, F2: 120, F3: 99, F4: 118, F5: 96, F6: 97, F7: 98, F8: 100, F9: 101, F10: 109, F11: 103, F12: 111,
};
/** Keys named by `event.code` that are not letters, digits or F-keys: the code, its virtual key and its label. */
const OTHER: Readonly<Record<string, readonly [number, string]>> = {
  Equal: [24, '='], Minus: [27, '-'], BracketRight: [30, ']'], BracketLeft: [33, '['], Quote: [39, "'"], Semicolon: [41, ';'],
  Backslash: [42, '\\'], Comma: [43, ','], Slash: [44, '/'], Period: [47, '.'], Backquote: [50, '`'],
  Space: [49, 'Space'], ArrowLeft: [123, '←'], ArrowRight: [124, '→'], ArrowDown: [125, '↓'], ArrowUp: [126, '↑'],
};

/** The virtual key code and the label of a physical key, or null for a key the shortcut cannot use. */
export function keyOf(code: string): { readonly keyCode: number; readonly label: string; readonly isFunction: boolean } | null {
  const letter = /^Key([A-Z])$/.exec(code)?.[1];
  if (letter !== undefined) {
    const keyCode = LETTERS[letter];
    return keyCode === undefined ? null : { keyCode, label: letter, isFunction: false };
  }
  const digit = /^Digit([0-9])$/.exec(code)?.[1];
  if (digit !== undefined) {
    const keyCode = DIGITS[digit];
    return keyCode === undefined ? null : { keyCode, label: digit, isFunction: false };
  }
  const functionKey = FUNCTION_KEYS[code];
  if (functionKey !== undefined) return { keyCode: functionKey, label: code, isFunction: true };
  const other = OTHER[code];
  return other === undefined ? null : { keyCode: other[0], label: other[1], isFunction: false };
}

/** Modifier symbols in the order the Mac writes them. */
function modifierSymbols(modifiers: number): string {
  return `${modifiers & MOD_CONTROL ? '⌃' : ''}${modifiers & MOD_OPTION ? '⌥' : ''}${modifiers & MOD_SHIFT ? '⇧' : ''}${modifiers & MOD_COMMAND ? '⌘' : ''}`;
}

export type Recorded = { readonly shortcut: Shortcut } | { readonly problem: string };

/**
 * The shortcut a key press makes, or the reason it cannot be one. A shortcut needs ⌘, ⌃ or ⌥ (a bare letter or
 * ⇧ and a letter would fire while typing everywhere); an F-key may stand alone. Null while only modifiers are down.
 */
export function recordShortcut(event: Pick<KeyboardEvent, 'code' | 'metaKey' | 'altKey' | 'ctrlKey' | 'shiftKey'>): Recorded | null {
  if (/^(Meta|Alt|Control|Shift)(Left|Right)$/.test(event.code)) return null;
  const key = keyOf(event.code);
  if (key === null) return { problem: t('macKeys.badKey') };
  const modifiers = (event.metaKey ? MOD_COMMAND : 0) | (event.altKey ? MOD_OPTION : 0) | (event.ctrlKey ? MOD_CONTROL : 0) | (event.shiftKey ? MOD_SHIFT : 0);
  if ((modifiers & (MOD_COMMAND | MOD_OPTION | MOD_CONTROL)) === 0 && !key.isFunction) {
    return { problem: t('macKeys.needModifier') };
  }
  return { shortcut: { keyCode: key.keyCode, modifiers, label: `${modifierSymbols(modifiers)}${key.label}` } };
}
